import type { Role } from "@/core/types";

import { PERMISSION_LIST } from "../constants/permission-list";
import type { PermissionContextMap, PermissionKey, PredicateContext } from "../types";

/**
 * Pure function to check if a user has a specific permission.
 * First checks role-based exclusions, then evaluates optional allowIf predicate
 *
 * @returns true if user has permission, false otherwise
 */
export type CheckPermissionByRoleProps<K extends PermissionKey> =
  PermissionContextMap[K] extends undefined
    ? { permissionKey: K; predicateContext?: never; role: Nullable<Role> } // TODO: use type built from src/config/roles.js
    : {
        permissionKey: K;
        predicateContext: PermissionContextMap[K];
        role: Nullable<Role>;
      };

export function checkPermissionByRole<K extends PermissionKey>({
  permissionKey,
  predicateContext,
  role,
}: CheckPermissionByRoleProps<K>): boolean {
  if (!role) return false;

  const permission = PERMISSION_LIST[permissionKey];

  if (!permission) return false;

  const isRoleAllowed = !permission.excludedRoles.includes(role);

  // If role is allowed, grant access
  if (isRoleAllowed) return true;

  // Role is excluded - check if allowIf predicate grants access
  if (permission.allowIf) {
    if (!predicateContext) return false;

    // Cast is safe: permissions with allowIf always have a corresponding
    // non-undefined entry in PermissionContextMap, enforced at call sites.
    return permission.allowIf(predicateContext as PredicateContext);
  }

  return false;
}
